import React, { useState, useEffect } from "react";
import {
  Table,
  Modal,
  Button,
  Badge,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
  Alert,
  Pagination,
} from "react-bootstrap";
import { FaSearch, FaRupeeSign, FaMapMarkerAlt, FaSync } from "react-icons/fa";
import {
  getAllJobs,
  addDistance,
  updateDistance,
  getDistance,
} from "../../../services/service";
import JobDetail from "./JobDetail";
import { toast } from "react-toastify";

// ------------------------------
// Status helpers
// ------------------------------
const normalizeStatusKey = (s) => {
  const k = (s || "").toLowerCase().replace(/\s|_/g, "-");
  if (
    ["in-progress", "inprogress", "in_progress", "ongoing", "active"].includes(
      k
    )
  )
    return "in-progress";
  if (["live"].includes(k)) return "live";
  if (["complete", "completed"].includes(k)) return "complete";
  if (["cancelled", "canceled"].includes(k)) return "cancelled";
  if (["pending", "open"].includes(k)) return "pending";
  if (["assigned"].includes(k)) return "assigned";
  if (["extended", "extension"].includes(k)) return "extended";
  if (["expired", "expire", "Expired"].includes(k)) return "expired";
  if (["paused", "pause"].includes(k)) return "paused";
  if (k === "no-show" || k === "noshow" || k === "no_show") return "no-show";
  return k || "unknown";
};

const humanizeStatus = (key) => {
  const map = {
    "in-progress": "In-progress",
    live: "Live",
    paused: "Paused",
    extended: "Extended",
    expired: "Expired",
    complete: "Complete",
    cancelled: "Cancelled",
    pending: "Pending",
    assigned: "Assigned",
    open: "Open",
    "no-show": "No Show",
    unknown: "Unknown",
  };
  return map[key] || key.charAt(0).toUpperCase() + key.slice(1);
};
const JOB_STATUS_COLORS = {
  "in-progress": "primary",
  live: "success",
  pending: "warning",
  complete: "success",
  extended: "info",
  expired: "dark",
  cancelled: "danger",
  assigned: "primary",
  paused: "secondary",
  "no-show": "#6f42c1",
  "No Show": "#6f42c1",
  unknown: "light",
};
const ALL_STATUS_OPTIONS = [
  { key: "Pending", label: "Pending" },
  { key: "In-progress", label: "In-progress" },
  { key: "live", label: "Live" },
  { key: "paused", label: "Paused" },
  { key: "extended", label: "Extended" },
  { key: "Expired", label: "Expired" },
  { key: "Complete", label: "Complete" },
  { key: "Cancelled", label: "Cancelled" },
  { key: "No Show", label: "No Show" },
];

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  // Format to Indian Standard Time (IST) and 12-hour clock
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

// ------------------------------
// Safe bilingual helpers
// ------------------------------
const getBilingualText = (obj, field) => {
  if (!obj || typeof obj !== "object") return "N/A";
  const value = obj[field];
  if (!value || typeof value !== "object")
    return (value || "").toString().trim() || "N/A";
  const en = value.en?.trim() || "";
  const hi = value.hi?.trim() || "";
  if (!en && !hi) return "N/A";
  if (en && hi) return `${en} (${hi})`;
  return en || hi;
};

const getSearchText = (obj, field) => {
  if (!obj || typeof obj !== "object") return "";
  const value = obj[field];
  if (!value || typeof value !== "object")
    return (value || "").toString().toLowerCase();
  const en = value.en?.trim() || "";
  const hi = value.hi?.trim() || "";
  return `${en} ${hi}`.toLowerCase();
};

// ------------------------------
// Component
// ------------------------------
export default function JobList() {
  const [showDistanceModal, setShowDistanceModal] = useState(false);
  const [distance, setDistance] = useState("");
  const [distanceId, setDistanceId] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloading, setDownloading] = useState(false);

  // ------------------------------
  // Fetch jobs
  // ------------------------------
  const fetchJobs = async (
    page = 1,
    searchVal = search,
    statusVal = statusFilter,
    startOverride = null,
    endOverride = null
  ) => {
    setLoading(true);
    setError("");
    try {
      const sDate = startOverride !== null ? startOverride : startDate;
      const eDate = endOverride !== null ? endOverride : endDate;

      const response = await getAllJobs({
        page,
        limit: pagination.limit,
        search: searchVal.trim(),
        status: statusVal,
        startDate: sDate || "",
        endDate: eDate || "",
      });
      if (response?.success) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.jobs || [];
        const transformedJobs = list.map((job) => {
          const statusKey = normalizeStatusKey(job.status);
          const statusLabel = humanizeStatus(statusKey);

          const priceNum = Number(job.price) || 0;
          const addressDisplay = getBilingualText(job, "address");
          const addressSearch = getSearchText(job, "address");
          const descriptionDisplay = getBilingualText(job, "description");
          const descriptionSearch = getSearchText(job, "description");

          // Equipment
          let equipmentDisplay = "N/A";
          let equipmentSearch = "";
          if (
            typeof job.equipment === "object" &&
            (job.equipment.en || job.equipment.hi)
          ) {
            equipmentDisplay = getBilingualText(
              { equipment: job.equipment },
              "equipment"
            );
            equipmentSearch = getSearchText(
              { equipment: job.equipment },
              "equipment"
            );
          } else if (typeof job.equipment === "string") {
            equipmentDisplay = job.equipment;
            equipmentSearch = job.equipment.toLowerCase();
          }

          // Crop Type
          let cropDisplay = "N/A";
          let cropSearch = "";
          if (
            typeof job.cropType === "object" &&
            (job.cropType.en || job.cropType.hi)
          ) {
            cropDisplay = getBilingualText(
              { cropType: job.cropType },
              "cropType"
            );
            cropSearch = getSearchText({ cropType: job.cropType }, "cropType");
          } else if (typeof job.cropType === "string") {
            cropDisplay = job.cropType;
            cropSearch = job.cropType.toLowerCase();
          }
          // Customer Name – FIXED: Don't pass {} fallback
          const customerNameObj = job.customer?.name || {};
          const customerName =
            getBilingualText(customerNameObj, "en") !== "N/A"
              ? getBilingualText(customerNameObj, "en")
              : job.customer?.name;

          // Vendor Name – FIXED: Only show "Not Assigned" if vendor is missing or name is empty
          const vendorNameObj = job.vendor?.name;
          const vendorName =
            vendorNameObj &&
              typeof vendorNameObj === "object" &&
              Object.keys(vendorNameObj).length > 0
              ? getBilingualText(vendorNameObj, "en")
              : typeof vendorNameObj === "string"
                ? vendorNameObj
                : "Not Assigned";

          // Cancellation Reason (new bilingual field)
          let cancellationReasonEn = "";
          let cancellationReasonHi = "";
          if (job.cancellationReason) {
            cancellationReasonEn =
              job.cancellationReason.en || "Reason Not Found";
            cancellationReasonHi =
              job.cancellationReason.hi || "कारण नहीं मिला";
          }
          // Cancellation Reason (new bilingual field)
          let pausedReasonEn = "";
          let pausedReasonHi = "";
          if (job.pausedReason) {
            pausedReasonEn = job.pausedReason.en || "Reason Not Found";
            pausedReasonHi = job.pausedReason.hi || "कारण नहीं मिला";
          }

          return {
            id: job._id || job.jobId,
            status: statusLabel,
            statusKey,
            equipment: equipmentDisplay,
            equipmentSearch,
            cropType: cropDisplay,
            cropSearch,
            customer: customerName,
            vendor: vendorName,
            equipmentDetails: job?.equipmentDetails,
            referralApplied: job?.referralApplied,
            referralDiscount: job?.referralDiscount,
            walletDiscountAmount: job?.walletDiscountAmount,
            customerDetails: {
              name: customerName,
              email: job.customer?.email || "N/A",
              phone: job.customer?.phone || "N/A",
              location: getBilingualText(job.customer || {}, "address"),
            },
            vendorDetails: {
              name: vendorName,
              email: job.vendor?.email || "N/A",
              phone: job.vendor?.phone || "N/A",
              location: getBilingualText(job.vendor || {}, "address"),
            },
            price: priceNum,
            quantity: job.areaTimeValue || 0,
            unit: job.areaTimeType || "N/A",
            address: addressDisplay,
            addressSearch,
            description: descriptionDisplay,
            descriptionSearch,
            distance: job?.distance,
            finalDeliveryCharge: job?.finalDeliveryCharge,
            deliveryCharge: job?.deliveryCharge,
            location: job.location?.area || "N/A",
            latitude: job.latitude ?? null,
            longitude: job.longitude ?? null,
            createdAt: formatDateTime(job.createdAt),
            updatedAt: formatDateTime(job.updatedAt),
            startedAt: formatDateTime(job.jobStartDateTime || job.startedAt),
            endedAt: formatDateTime(job.endedAt),
            paymentStatus: job.paymentStatus || "N/A",
            reviewStatus: job.reviewStatus || "N/A",
            paymentDetails: job.paymentDetails || [],
            location: job?.location || {},
            rawData: job,
            quote: job.quote || [],
            deliveryCharge: job.deliveryCharge || 0,
            activities: job?.activities,
            extensions: job?.extensions,
            pausedReason: {
              en: pausedReasonEn,
              hi: pausedReasonHi,
            },

            cancellationReason: {
              en: cancellationReasonEn,
              hi: cancellationReasonHi,
            },
            extensionEndDateTime: job.extensionEndDateTime || "Not Available",
            extensionStartDateTime:
              job.extensionStartDateTime || "Not Available",
            extensionrequestStartDateTime:
              job.extensionrequestStartDateTime || "Not Available",
            jobPausedTime: job.jobPausedTime || "Not Available",
            commissionAmount: job?.commissionAmount,
            customerPayout: job?.customerPayout,
            vendorPayout: job?.vendorPayout,
            hourUsed: job?.hourUsed,
            UsedTime: job?.UsedTime,
            tds: job?.tds,
            cgst: job?.cgst,
            jobDateTime: job?.jobDateTime,
            sgst: job?.sgst,
            photos: job?.photos || [],
            descriptionEn: job?.description?.en || "N/A",
          };
        });

        setJobs(transformedJobs);
        setPagination({
          page: response.page ?? 1,
          limit: response.limit ?? pagination.limit,
          total: response.total ?? transformedJobs.length,
          pages: response.pages ?? 1,
        });
      } else {
        setJobs([]);
        setPagination((p) => ({ ...p, total: 0, pages: 0 }));
      }
    } catch (err) {
      setError("Failed to fetch jobs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadJobCSV = async () => {
    try {
      setDownloading(true);
      const response = await getAllJobs({
        page: 1,
        limit: 100000,
        search: search.trim(),
        status: statusFilter,
        startDate: startDate || "",
        endDate: endDate || "",
      });

      if (response?.success) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.jobs || [];

        const csvRows = [
          [
            "JobID",
            "Status",
            "CropType",
            "Equipment",
            "CustomerName",
            "CustomerPhone",
            "VendorName",
            "VendorPhone",
            "Location",
            "Price",
            "Quantity",
            "Unit",
            "CreatedAt",
            "StartedAt",
            "EndedAt",
            "DeliveryCharge",
            "CommissionAmount",
            "VendorPayout",
            "CustomerPayout",
            "PaymentStatus",
          ],
        ];

        list.forEach((job) => {
          // Normalize names using the bilingual helper
          const customerName = getBilingualText(job.customer, "name");
          const customerPhone = job.customer?.phone || "N/A";
          const vendorName = job.vendor?._id
            ? getBilingualText(job.vendor, "name")
            : "Not Assigned";
          const vendorPhone = job.vendor?.phone || "N/A";

          const row = [
            `"${job.jobId || job._id}"`,
            `"${job.status || "N/A"}"`,
            `"${job.cropType || "N/A"}"`,
            `"${job.equipment || "N/A"}"`,
            `"${customerName}"`,
            `"${customerPhone}"`,
            `"${vendorName}"`,
            `"${vendorPhone}"`,
            `"${getBilingualText(job, "address")}"`,
            job.price || 0,
            job.areaTimeValue || 0,
            `"${job.areaTimeType || "N/A"}"`,
            `"${formatDateTime(job.createdAt)}"`,
            `"${formatDateTime(job.startedAt || job.jobStartDateTime)}"`,
            `"${formatDateTime(job.endedAt || job.jobEndDateTime)}"`,
            job.finalDeliveryCharge || 0,
            job.commissionAmount || 0,
            job.vendorPayout || 0,
            job.customerPayout || 0,
            `"${job.paymentStatus || "Pending"}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `job_list_${new Date().toISOString()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("Failed to fetch jobs for CSV export");
      }
    } catch (err) {
      console.error("CSV Download Error:", err);
      toast.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchJobs(1, search, statusFilter);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, statusFilter]);

  // ------------------------------
  // Filtering + pagination
  // ------------------------------
  const filteredJobs = jobs;

  const handleShow = (job) => {
    setSelectedJob(job);
    setShow(true);
  };
  const handleClose = () => {
    setSelectedJob(null);
    setShow(false);
  };
  const handlePageChange = (newPage) => {
    fetchJobs(newPage);
  };

  // ------------------------------
  // Distance management
  // ------------------------------
  useEffect(() => {
    const fetchDistance = async () => {
      try {
        const res = await getDistance();
        if (res) {
          setDistance(res.distance);
          setDistanceId(res._id);
        }
      } catch (err) {
        console.error("Failed to fetch distance:", err);
      }
    };
    fetchDistance();
  }, []);

  const handleDistanceSubmit = async (e) => {
    e.preventDefault();
    const numericDistance = Number(distance);
    if (!distance || isNaN(numericDistance) || numericDistance < 0) {
      toast.success("Please enter a valid distance number");
      return;
    }

    try {
      if (distanceId) {
        await updateDistance(distanceId, numericDistance);
        toast.success("Distance updated successfully");
      } else {
        const res = await addDistance(numericDistance);
        if (res?._id) setDistanceId(res._id);
        toast.success("Distance saved successfully");
      }
      setDistance(numericDistance);
      setShowDistanceModal(false);
    } catch (err) {
      toast.error(
        "Failed to save distance: " +
        (err?.response?.data?.message || err.message)
      );
    }
  };

  // ------------------------------
  // UI Rendering
  // ------------------------------
  return (
    <div style={{ position: "relative", zIndex: 1 }}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Row className="align-items-center mb-3 g-3">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            {ALL_STATUS_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              className="form-control-sm"
              style={{ width: "150px", cursor: "pointer" }}
            />
            <span className="text-muted">-</span>
            <Form.Control
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              className="form-control-sm"
              style={{ width: "150px", cursor: "pointer" }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchJobs(1, search, statusFilter)}
            >
              Apply
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setStartDate("");
                setEndDate("");
                fetchJobs(1, search, statusFilter, "", "");
              }}
            >
              Clear
            </Button>
          </div>
        </Col>
        <Col md={3} className="text-end">
          <Button
            variant="primary"
            size="sm"
            onClick={downloadJobCSV}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Downloading...
              </>
            ) : (
              "Download CSV"
            )}
          </Button>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading jobs...</p>
        </div>
      )}

      <div className="row g-2 align-items-center mb-3">
        <div className="col-12 col-md-6">
          <div className="d-flex flex-wrap gap-2">
            <Button variant="light" size="sm" disabled>
              Total Jobs: {pagination.total}
            </Button>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="d-flex flex-wrap justify-content-md-end gap-2 align-items-center">
            <Button variant="light" size="sm" disabled>
              Current Distance: {distance ? `${distance} km` : "Not set"}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fetchJobs(pagination.page, search, statusFilter)}
              disabled={loading}
            >
              <FaSync className={loading ? "fa-spin me-1" : "me-1"} />
              Refresh
            </Button>
            <Button
              variant={!distanceId ? "primary" : "warning"}
              size="sm"
              onClick={() => setShowDistanceModal(true)}
            >
              Set Distance
            </Button>
          </div>
        </div>
      </div>

      {!loading && (
        <>
          <Table hover responsive className="align-middle shadow-sm rounded">
            <thead className="table-light">
              <tr>
                <th>Status</th>
                <th>Crop Type</th>
                <th>Equipment</th>
                <th>Customer</th>
                <th>Vendor</th>
                <th>Location</th>
                <th>Rental Mode</th>
                <th>Quote</th>
                <th>Created At</th>
                <th>Started At</th>
                <th>Ended At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length ? (
                filteredJobs.map((job) => (
                  <tr
                    key={job.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleShow(job)}
                  >
                    <td>
                      <Badge
                        bg={JOB_STATUS_COLORS[job.statusKey]?.startsWith("#") ? "" : (JOB_STATUS_COLORS[job.statusKey] || "secondary")}
                        style={JOB_STATUS_COLORS[job.statusKey]?.startsWith("#") ? { backgroundColor: JOB_STATUS_COLORS[job.statusKey] } : {}}
                      >
                        {job.status}
                      </Badge>
                    </td>
                    <td>{job.cropType}</td>
                    <td>{job.equipment}</td>
                    <td>{job.customer}</td>
                    <td>{job.vendor}</td>
                    <td>
                      <FaMapMarkerAlt className="me-1" />
                      {job.address}
                    </td>
                    <td>
                      {job.quantity} {job.unit}
                    </td>
                    <td>
                      <FaRupeeSign className="me-1" />
                      {typeof job.price === "number"
                        ? job.price.toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      {job.createdAt || <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {job.startedAt || <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {job.endedAt || <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShow(job);
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="text-center text-muted py-4">
                    No jobs found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {pagination.pages > 1 && (
            <Pagination className="mt-3 justify-content-center">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(Math.max(pagination.page - 1, 1))}
                disabled={pagination.page === 1}
              />

              {/* Show first page if not in window */}
              {pagination.page > 3 && (
                <>
                  <Pagination.Item onClick={() => handlePageChange(1)}>
                    1
                  </Pagination.Item>
                  {pagination.page > 4 && <Pagination.Ellipsis disabled />}
                </>
              )}

              {/* Window of pages around current page */}
              {[...Array(pagination.pages)].map((_, i) => {
                const page = i + 1;
                if (
                  page >= pagination.page - 2 &&
                  page <= pagination.page + 2
                ) {
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === pagination.page}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                }
                return null;
              })}

              {/* Show last page if not in window */}
              {pagination.page < pagination.pages - 2 && (
                <>
                  {pagination.page < pagination.pages - 3 && (
                    <Pagination.Ellipsis disabled />
                  )}
                  <Pagination.Item
                    onClick={() => handlePageChange(pagination.pages)}
                  >
                    {pagination.pages}
                  </Pagination.Item>
                </>
              )}

              <Pagination.Next
                onClick={() =>
                  handlePageChange(
                    Math.min(pagination.page + 1, pagination.pages)
                  )
                }
                disabled={pagination.page === pagination.pages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(pagination.pages)}
                disabled={pagination.page === pagination.pages}
              />
            </Pagination>
          )}
        </>
      )}

      <Modal show={show} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Job Details{" "}
            <div
              className="text-muted"
              style={{ fontSize: "15px", lineHeight: "1.2" }}
            >
              Job Id: {selectedJob?.id}
            </div>{" "}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedJob && (
            <JobDetail
              job={selectedJob.rawData || selectedJob}
              onRefresh={() => {
                fetchJobs(pagination.page);
                handleClose();
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDistanceModal}
        onHide={() => setShowDistanceModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Distance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleDistanceSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Distance (in km)</Form.Label>
              <Form.Control
                type="number"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                min={0}
              />
            </Form.Group>
            <Button type="submit" variant="primary">
              Save Distance
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

// import React, { useState, useEffect } from 'react';
// import { Table, Modal, Button, Badge, Form, Row, Col, InputGroup, Spinner, Alert } from 'react-bootstrap';
// import { FaSearch, FaRupeeSign, FaMapMarkerAlt } from 'react-icons/fa';
// import { getAllJobs, getAllCommissions } from '../../../services/service';
// import JobDetail from './JobDetail';

// // status helpers
// const normalizeStatusKey = (s) => {
//   const k = (s || '').toLowerCase().replace(/\s|_/g, '-');
//   if (['in-progress', 'inprogress', 'in_progress', 'ongoing', 'active'].includes(k)) return 'in-progress';
//   if (['live'].includes(k)) return 'live';
//   if (['complete', 'completed'].includes(k)) return 'complete';
//   if (['cancelled', 'canceled'].includes(k)) return 'cancelled';
//   if (['pending', 'open'].includes(k)) return 'pending';
//   if (['assigned'].includes(k)) return 'assigned';
//   return k || 'unknown';
// };
// const humanizeStatus = (key) => {
//   const map = {
//     'in-progress': 'In-progress',
//     'live': 'Live',
//     'complete': 'Complete',
//     'cancelled': 'Cancelled',
//     'pending': 'Pending',
//     'assigned': 'Assigned',
//     'open': 'Open',
//     'unknown': 'Unknown'
//   };
//   return map[key] || (key.charAt(0).toUpperCase() + key.slice(1));
// };

// const JOB_STATUS_COLORS = {
//   'in-progress': 'info',
//   'live': 'info',
//   'pending': 'warning',
//   'complete': 'success',
//   'cancelled': 'danger',
//   'assigned': 'primary',
//   'open': 'secondary',
//   'unknown': 'secondary'
// };

// // Fixed list matching your backend statuses exactly
// const ALL_STATUS_OPTIONS = [
//   { key: 'pending', label: 'Pending' },
//   { key: 'in-progress', label: 'In-progress' },
//   { key: 'live', label: 'Live' },
//   { key: 'complete', label: 'Complete' },
//   { key: 'cancelled', label: 'Cancelled' },
// ];

// // ✅ Date formatting fix (handles when only date is sent from API)
// const formatDateTime = (dateStr) => {
//   if (!dateStr) return '';
//   const date = new Date(dateStr);

//   // If invalid date, return raw string
//   if (isNaN(date.getTime())) return dateStr;

//   // Show both date & time properly
//   return new Intl.DateTimeFormat('en-IN', {
//     year: 'numeric',
//     month: 'short',
//     day: '2-digit',
//     hour: '2-digit',
//     minute: '2-digit'
//   }).format(date);
// };

// export default function JobList() {
//   const [showDistanceModal, setShowDistanceModal] = useState(false);
//   const [distance, setDistance] = useState('');
//   const [show, setShow] = useState(false);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [search, setSearch] = useState('');
//   const [statusFilter, setStatusFilter] = useState(''); // stores normalized status key
//   const [jobs, setJobs] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState('');
//   const [pagination, setPagination] = useState({
//     page: 1,
//     limit: 20,
//     total: 0,
//     pages: 0
//   });

//   // Fetch jobs from API
//   const fetchJobs = async (page = 1) => {
//     setLoading(true);
//     setError('');
//     try {
//       const response = await getAllJobs(page, pagination.limit);
//       if (response?.success) {
//         const list = Array.isArray(response.data) ? response.data : (response.data?.jobs || []);
//         const transformedJobs = list.map(job => {
//           const statusKey = normalizeStatusKey(job.status);
//           const statusLabel = humanizeStatus(statusKey);
//           const priceNum =
//             typeof job.price === 'number'
//               ? job.price
//               : (Array.isArray(job.quote) && job.quote[0]?.quote ? Number(job.quote[0].quote) : 0);

//           return {
//             id: job.jobId,
//             status: statusLabel,
//             statusKey, // used for filtering & badge color
//             equipment: job.equipment,
//             cropType: job.cropType,
//             customer: job.customer?.name || 'N/A',
//             vendor: job.vendor?.name || 'Not Assigned',
//             customerDetails: {
//               name: job.customer?.name || 'N/A',
//               email: job.customer?.email || 'N/A',
//               phone: job.customer?.phone || 'N/A',
//               location: job.location?.area || 'N/A'
//             },
//             vendorDetails: {
//               name: job.vendor?.name || 'Not Assigned',
//               email: job.vendor?.email || 'N/A',
//               phone: job.vendor?.phone || 'N/A'
//             },
//             // Quote column shows PRICE
//             price: priceNum,
//             quantity: job.areaTimeValue || 0,
//             unit: job.areaTimeType || 'N/A',
//             location: job.location?.area || 'N/A',
//               // 🆕 Add Lat/Long keys
//             latitude: job.location?.latitude || null,
//             longitude: job.location?.longitude || null,
//             // 🆕 Dates (fixed formatting)
//             createdAt: formatDateTime(job.createdAt),
//             startedAt: formatDateTime(job.startedAt),
//             endedAt: formatDateTime(job.endedAt),
//             paymentStatus: job.paymentStatus || 'N/A',
//             reviewStatus: job.reviewStatus || 'N/A',
//             rawData: job,
//             quote: job.quote || []
//           };
//         });

//         setJobs(transformedJobs);
//         setPagination({
//           page: response.page ?? 1,
//           limit: response.limit ?? pagination.limit,
//           total: response.total ?? transformedJobs.length,
//           pages: response.pages ?? 1
//         });
//       } else {
//         setJobs([]);
//         setPagination(p => ({ ...p, total: 0, pages: 0 }));
//       }
//     } catch (err) {
//       setError('Failed to fetch jobs: ' + err.message);
//       console.error('Fetch jobs error:', err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => { fetchJobs(); /* eslint-disable-next-line */ }, []);

//   // Filter/search logic (✅ safe toLowerCase checks)
//   const filteredJobs = jobs.filter(job =>
//     (statusFilter ? job.statusKey === statusFilter : true) &&
//     (
//       (job.id && job.id.toLowerCase().includes(search.toLowerCase())) ||
//       (job.customer && job.customer.toLowerCase().includes(search.toLowerCase())) ||
//       (job.vendor && job.vendor.toLowerCase().includes(search.toLowerCase())) ||
//       (job.location && job.location.toLowerCase().includes(search.toLowerCase()))
//     )
//   );

//   const handleShow = job => { setSelectedJob(job); setShow(true); };
//   const handleClose = () => { setSelectedJob(null); setShow(false); };

//   const handlePageChange = (newPage) => {
//     fetchJobs(newPage);
//   };

//   useEffect(() => {
//     const fetchAndStoreCommission = async () => {
//       try {
//         const res = await getAllCommissions();
//         if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
//           const commission = res.data[0];
//           localStorage.setItem("commissionType", commission.commissionType);
//           localStorage.setItem("commissionValue", commission.typeValue);
//         }
//       } catch (error) {
//         console.error("Failed to fetch commission:", error);
//       }
//     };

//     fetchAndStoreCommission();
//   }, []);

//   return (
//     <>
//       {/* Error Alert */}
//       {error && (
//         <Alert variant="danger" dismissible onClose={() => setError('')}>
//           {error}
//         </Alert>
//       )}

//       {/* Filters and Search */}
//       <Row className="align-items-center mb-3">
//         <Col md={4}>
//           <InputGroup>
//             <InputGroup.Text><FaSearch /></InputGroup.Text>
//             <Form.Control
//               placeholder="Search by Job ID, customer, vendor…"
//               value={search}
//               onChange={e => setSearch(e.target.value)}
//             />
//           </InputGroup>
//         </Col>
//         <Col md={3} style={{ zIndex: 100 }}>
//           <Form.Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
//             <option value="">All Status</option>
//             {ALL_STATUS_OPTIONS.map(o => (
//               <option key={o.key} value={o.key}>{o.label}</option>
//             ))}
//           </Form.Select>
//         </Col>
//         <Col md={5} className="text-end">
//           <small className="text-muted">
//             Showing {filteredJobs.length} of {pagination.total} jobs
//           </small>
//         </Col>
//       </Row>

//       {/* Loading Spinner */}
//       {loading && (
//         <div className="text-center py-5">
//           <Spinner animation="border" />
//           <p className="mt-2">Loading jobs...</p>
//         </div>
//       )}

//       {/* Top Bar */}
// <div className="row g-2 align-items-center mb-3">
//   <div className="col-12 col-md-6">
//     <div className="d-flex flex-wrap gap-2">
//       <Button variant="light" size="sm" disabled>
//         Total Jobs: {filteredJobs.length}
//       </Button>
//     </div>
//   </div>

//   <div className="col-12 col-md-6">
//     <div className="d-flex flex-wrap justify-content-md-end gap-2">
//       <Button
//         variant="primary"
//         size="sm"
//         onClick={() => setShowDistanceModal(true)}
//       >
//         Set Distance
//       </Button>
//     </div>
//   </div>
// </div>

//       {/* Job Table */}
//       {!loading && (
//         <>
//           <Table hover responsive className="align-middle shadow-sm rounded">
//             <thead className="table-light">
//               <tr>
//                 <th>Status</th>
//                 <th>CropType</th>
//                 <th>Equipment</th>
//                 <th>Customer</th>
//                 <th>Vendor</th>
//                 <th>Location</th>
//                 <th>Rental Mode</th>
//                 <th>Quote</th>
//                 <th>Created At</th>   {/* 🆕 */}
//                 <th>Started At</th>
//                 <th>Ended At</th>     {/* 🆕 */}
//                 <th>Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredJobs.length ? filteredJobs.map(job => (
//                 <tr key={job.id} style={{ cursor: 'pointer' }} onClick={() => handleShow(job)}>
//                   <td><Badge bg={JOB_STATUS_COLORS[job.statusKey] || 'secondary'}>{job.status}</Badge></td>
//                   <td>{job.cropType}</td>
//                   <td>{job.equipment}</td>
//                   <td>{job.customer}</td>
//                   <td>{job.vendor}</td>
//                   <td><FaMapMarkerAlt className="me-1" />{job.location}</td>
//                   <td>{job.quantity} {job.unit}</td>
//                   <td><FaRupeeSign className="me-1" />{typeof job.price === 'number' ? job.price.toLocaleString() : 'N/A'}</td>

//                   {/* 🆕 Dates */}
//                   <td>{job.createdAt || <span className="text-muted">—</span>}</td>
//                   <td>{job.startedAt || <span className="text-muted">—</span>}</td>
//                   <td>{job.endedAt || <span className="text-muted">—</span>}</td>

//                   <td>
//                     <Button size="sm" variant="outline-primary" onClick={e => { e.stopPropagation(); handleShow(job); }}>View</Button>
//                   </td>
//                 </tr>
//               )) : (
//                 <tr>
//                   <td colSpan={12} className="text-center text-muted py-4">No jobs found.</td>
//                 </tr>
//               )}
//             </tbody>

//           </Table>

//           {/* Pagination */}
//           <div className="d-flex justify-content-center my-3">
//           {pagination.pages > 1 && (
//             <div className="d-flex justify-content-center mt-3">
//               <Button
//                 variant="outline-primary"
//                 size="sm"
//                 className="me-2"
//                 disabled={pagination.page === 1}
//                 onClick={() => handlePageChange(pagination.page - 1)}
//               >
//                 Previous
//               </Button>
//               <span className="align-self-center mx-3">
//                 Page {pagination.page} of {pagination.pages}
//               </span>
//               <Button
//                 variant="outline-primary"
//                 size="sm"
//                 disabled={pagination.page === pagination.pages}
//                 onClick={() => handlePageChange(pagination.page + 1)}
//               >
//                 Next
//               </Button>
//             </div>
//           )}
//           </div>
//         </>
//       )}

//       <Modal show={show} onHide={handleClose} size="xl">
//         <Modal.Header closeButton>
//           <Modal.Title>Job Details — {selectedJob?.id}</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           {selectedJob && <JobDetail job={selectedJob} />}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={handleClose}>Close</Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Top Bar */}
// <div className="row g-2 align-items-center mb-3">
//   <div className="col-12 col-md-6">
//     <div className="d-flex flex-wrap gap-2">
//       <Button variant="light" size="sm" disabled>
//         Total Jobs: {filteredJobs.length}
//       </Button>
//     </div>
//   </div>

//   <div className="col-12 col-md-6">
//     <div className="d-flex flex-wrap justify-content-md-end gap-2">
//       <Button
//         variant="primary"
//         size="sm"
//         onClick={() => setShowDistanceModal(true)}
//       >
//         Set Distance
//       </Button>
//     </div>
//   </div>
// </div>

// {/* Distance Modal */}
// <Modal
//   show={showDistanceModal}
//   onHide={() => setShowDistanceModal(false)}
//   centered
// >
//   <Modal.Header closeButton>
//     <Modal.Title>Set Distance</Modal.Title>
//   </Modal.Header>

//   <Modal.Body>
//     <Form onSubmit={handleDistanceSubmit}>
//       <Form.Group className="mb-3">
//         <Form.Label>Distance (in km)</Form.Label>
//         <Form.Control
//           type="number"
//           name="distance"
//           value={distance || ""}
//           onChange={(e) => setDistance(e.target.value)}
//           required
//           min={0}
//         />
//       </Form.Group>

//       <Button type="submit" variant="primary">
//         Save Distance
//       </Button>
//     </Form>
//   </Modal.Body>
// </Modal>

//     </>
//   );
// }
